import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DroneService } from '../../core/services/drone.service';
import {
  DroneInventory,
  getDroneStatusBadgeClass,
  getDroneStatusLabel,
} from '../../core/models/drone.model';

/**
 * Assign Drone Modal Component
 *
 * Modal component for assigning a drone to an organization.
 * Allows searching and selecting from available drones.
 */
@Component({
  selector: 'app-assign-drone-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './assign-drone-modal.component.html',
  styleUrls: ['./assign-drone-modal.component.scss'],
})
export class AssignDroneModalComponent implements OnInit {
  @Input() organizationId!: string;
  @Input() organizationName!: string;
  @Output() droneAssigned = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  availableDrones: DroneInventory[] = [];
  filteredDrones: DroneInventory[] = [];
  selectedDrone: DroneInventory | null = null;
  searchQuery = '';
  notes = '';
  loading = true;
  saving = false;
  errorMessage = '';

  // Helper functions for template
  getDroneStatusBadgeClass = getDroneStatusBadgeClass;
  getDroneStatusLabel = getDroneStatusLabel;

  // Counts for display
  assignableCount = 0;
  unassignableCount = 0;

  constructor(private droneService: DroneService) {}

  /**
   * Check if a drone can be assigned (has N-Number)
   */
  canAssignDrone(drone: DroneInventory): boolean {
    return !!drone.nNumber;
  }

  /**
   * Get the reason why a drone can't be assigned
   */
  getUnassignableReason(drone: DroneInventory): string {
    if (!drone.nNumber) {
      return 'Missing FAA N-Number registration';
    }
    return '';
  }

  ngOnInit(): void {
    this.loadAvailableDrones();
  }

  /**
   * Load available drones (not currently assigned)
   */
  async loadAvailableDrones(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      this.availableDrones = await this.droneService
        .getAvailableDrones()
        .toPromise()
        .then((data) => data || []);
      this.filteredDrones = this.sortDrones([...this.availableDrones]);
      this.updateCounts();
    } catch (error: any) {
      console.error('Failed to load available drones:', error);
      this.errorMessage = 'Failed to load available drones';
      this.availableDrones = [];
      this.filteredDrones = [];
    } finally {
      this.loading = false;
    }
  }

  /**
   * Update assignable/unassignable counts
   */
  private updateCounts(): void {
    this.assignableCount = this.filteredDrones.filter(d => this.canAssignDrone(d)).length;
    this.unassignableCount = this.filteredDrones.filter(d => !this.canAssignDrone(d)).length;
  }

  /**
   * Sort drones so registered (with nNumber) appear first
   */
  private sortDrones(drones: DroneInventory[]): DroneInventory[] {
    return drones.sort((a, b) => {
      // Registered drones (with nNumber) come first
      const aRegistered = !!a.nNumber;
      const bRegistered = !!b.nNumber;

      if (aRegistered && !bRegistered) return -1;
      if (!aRegistered && bRegistered) return 1;

      // Within the same group, sort by serial number
      return a.serialNumber.localeCompare(b.serialNumber);
    });
  }

  /**
   * Filter drones based on search query
   */
  onSearchChange(): void {
    const query = this.searchQuery.toLowerCase().trim();
    let filtered: DroneInventory[];
    if (!query) {
      filtered = [...this.availableDrones];
    } else {
      filtered = this.availableDrones.filter((drone) => {
        return (
          drone.serialNumber.toLowerCase().includes(query) ||
          drone.nNumber?.toLowerCase().includes(query) ||
          drone.name.toLowerCase().includes(query) ||
          drone.manufacturerName?.toLowerCase().includes(query) ||
          drone.modelName?.toLowerCase().includes(query)
        );
      });
    }
    this.filteredDrones = this.sortDrones(filtered);
    this.updateCounts();
  }

  /**
   * Select a drone (only if assignable)
   */
  selectDrone(drone: DroneInventory): void {
    if (!this.canAssignDrone(drone)) {
      // Don't select unassignable drones
      return;
    }
    this.selectedDrone = drone;
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    this.selectedDrone = null;
  }

  /**
   * Assign the selected drone to the organization
   */
  async assignDrone(): Promise<void> {
    if (!this.selectedDrone) {
      this.errorMessage = 'Please select a drone';
      return;
    }

    this.saving = true;
    this.errorMessage = '';

    try {
      await this.droneService
        .assignDrone(
          this.selectedDrone.id,
          this.organizationId,
          undefined,
          this.notes || undefined
        )
        .toPromise();

      // Emit success event
      this.droneAssigned.emit();
      this.close();
    } catch (error: any) {
      console.error('Failed to assign drone:', error);
      this.errorMessage = error.message || 'Failed to assign drone';
    } finally {
      this.saving = false;
    }
  }

  /**
   * Close the modal
   */
  close(): void {
    this.closed.emit();
  }

  /**
   * Get drone display name
   */
  getDroneDisplayName(drone: DroneInventory): string {
    const manufacturer = drone.manufacturerName || 'Unknown';
    const model = drone.modelName || 'Unknown Model';
    return `${manufacturer} ${model}`;
  }
}
